import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Play, FileText, Mic, Star, Clock, Upload, Plus, X, Video } from 'lucide-react';
import AppShell from '../components/AppShell';
import { FadeUp } from '../components/Animations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_CONFIG = {
  interview: { icon: Mic, label: 'Interview', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  article: { icon: FileText, label: 'Article', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  spotlight: { icon: Star, label: 'Spotlight', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  video: { icon: Play, label: 'Video', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

export default function PowerVault() {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showAdminUpload, setShowAdminUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', content_type: 'article', featured: false });
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');
  const isAdmin = user.role === 'admin';

  useEffect(() => { loadContent(); }, []);
  const loadContent = async () => {
    try { const r = await axios.get(`${API}/vault/content`); setContent(r.data); } catch(e) {}
    setLoading(false);
  };

  const handleUpload = async () => {
    if (!uploadForm.title.trim()) { toast.error('Title is required'); return; }
    setUploading(true);
    try {
      let mediaUrl = '';
      if (videoFile) {
        const formData = new FormData();
        formData.append('file', videoFile);
        const uploadRes = await axios.post(`${API}/upload/video`, formData);
        mediaUrl = uploadRes.data.url;
      }
      await axios.post(`${API}/vault/content`, { ...uploadForm, media_url: mediaUrl });
      toast.success('Content added!');
      setShowAdminUpload(false);
      setUploadForm({ title: '', description: '', content_type: 'article', featured: false });
      setVideoFile(null);
      loadContent();
    } catch(e) { toast.error('Upload failed'); }
    setUploading(false);
  };

  const featured = content.filter(c => c.featured);
  const filtered = filter === 'all' ? content : content.filter(c => c.content_type === filter);

  return (
    <AppShell>
      {/* White background content area */}
      <div className="min-h-screen bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">

          {/* Header */}
          <FadeUp>
            <div className="text-center mb-10">
              <h1 className="text-4xl sm:text-5xl font-cormorant font-bold text-gray-900 mb-3" data-testid="vault-title">Power Vault</h1>
              <p className="text-gray-500 font-inter text-base max-w-lg mx-auto">Exclusive interviews, insights, and member spotlights curated for your journey.</p>
              {isAdmin && (
                <div className="mt-5 flex items-center justify-center space-x-3">
                  <button onClick={() => { setUploadForm(f => ({...f, content_type: 'article'})); setShowAdminUpload(true); }}
                    className="inline-flex items-center space-x-2 bg-gray-900 text-white font-inter text-sm font-medium px-5 py-2.5 rounded-full hover:bg-gray-800 transition-colors"
                    data-testid="admin-upload-btn">
                    <Plus className="w-4 h-4" /><span>Add Article</span>
                  </button>
                  <button onClick={() => { setUploadForm(f => ({...f, content_type: 'video'})); setShowAdminUpload(true); }}
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter text-sm font-medium px-5 py-2.5 rounded-full hover:opacity-90 transition-colors shadow-lg shadow-purple-500/20"
                    data-testid="admin-video-upload-btn">
                    <Video className="w-4 h-4" /><span>Upload Video</span>
                  </button>
                </div>
              )}
            </div>
          </FadeUp>

          {/* Admin Upload Modal */}
          {showAdminUpload && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowAdminUpload(false)}>
              <div className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()} data-testid="admin-upload-modal">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-cormorant font-bold text-gray-900">Add Content</h2>
                  <button onClick={() => setShowAdminUpload(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-inter font-medium mb-1">Title</label>
                    <input value={uploadForm.title} onChange={e => setUploadForm(prev => ({...prev, title: e.target.value}))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 font-inter text-sm outline-none focus:border-indigo-500"
                      placeholder="Article title..." data-testid="upload-title" />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-inter font-medium mb-1">Description</label>
                    <textarea value={uploadForm.description} onChange={e => setUploadForm(prev => ({...prev, description: e.target.value}))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-gray-900 font-inter text-sm outline-none focus:border-indigo-500 min-h-[100px] resize-none"
                      placeholder="Content description..." data-testid="upload-description" />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-inter font-medium mb-1">Type</label>
                    <div className="flex flex-wrap gap-2">
                      {['article', 'interview', 'spotlight', 'video'].map(t => (
                        <button key={t} onClick={() => setUploadForm(prev => ({...prev, content_type: t}))}
                          className={`px-4 py-2 rounded-full text-sm font-inter font-medium border transition-all ${
                            uploadForm.content_type === t ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                          }`} data-testid={`upload-type-${t}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
                      ))}
                    </div>
                  </div>
                  {uploadForm.content_type === 'video' && (
                    <div>
                      <label className="block text-gray-700 text-sm font-inter font-medium mb-1">Video File</label>
                      <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
                        {videoFile ? (
                          <div className="flex items-center justify-center space-x-2">
                            <Video className="w-5 h-5 text-gray-500" />
                            <span className="text-gray-700 font-inter text-sm">{videoFile.name}</span>
                            <button onClick={() => setVideoFile(null)} className="text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-gray-500 text-sm font-inter">Click to upload video</p>
                            <p className="text-gray-400 text-xs font-inter mt-1">MP4, WebM, MOV</p>
                            <input type="file" accept="video/*" className="hidden" onChange={e => setVideoFile(e.target.files[0])} data-testid="video-file-input" />
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <input type="checkbox" checked={uploadForm.featured}
                      onChange={e => setUploadForm(prev => ({...prev, featured: e.target.checked}))}
                      className="w-4 h-4 rounded border-gray-300" data-testid="upload-featured" />
                    <label className="text-gray-600 text-sm font-inter">Featured content</label>
                  </div>
                  <button onClick={handleUpload} disabled={uploading || !uploadForm.title.trim()}
                    className="w-full bg-gray-900 text-white font-inter font-bold py-3 rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-all"
                    data-testid="upload-submit">
                    {uploading ? 'Uploading...' : 'Publish Content'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Featured */}
          {featured.length > 0 && (
            <FadeUp delay={0.1}>
              <div className="mb-12">
                <h2 className="text-xs font-inter font-bold uppercase tracking-[0.2em] text-gray-400 mb-4">Featured</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {featured.map((item, i) => {
                    const tc = TYPE_CONFIG[item.content_type] || TYPE_CONFIG.article;
                    const Icon = tc.icon;
                    return (
                      <motion.div key={i} whileHover={{ y: -4 }}
                        className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all cursor-pointer overflow-hidden group"
                        onClick={() => setSelectedArticle(item)} data-testid={`featured-content-${i}`}>
                        <div className="h-2 bg-gradient-to-r from-indigo-500 to-rose-500" />
                        <div className="p-7">
                          <div className="flex items-center space-x-2 mb-4">
                            <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-inter font-medium border ${tc.color}`}>
                              <Icon className="w-3 h-3" /><span>{tc.label}</span>
                            </span>
                            <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-full text-xs font-inter font-medium flex items-center space-x-1">
                              <Star className="w-3 h-3" /><span>Featured</span>
                            </span>
                          </div>
                          <h3 className="text-xl font-cormorant font-bold text-gray-900 mb-2 group-hover:text-indigo-700 transition-colors">{item.title}</h3>
                          <p className="text-gray-500 font-inter text-sm leading-relaxed line-clamp-3">{item.description}</p>
                          <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
                            <span className="text-gray-400 text-xs font-inter">{item.author}</span>
                            <span className="text-gray-400 text-xs font-inter flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span>{new Date(item.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </FadeUp>
          )}

          {/* Filter Tabs */}
          <FadeUp delay={0.2}>
            <div className="flex items-center space-x-2 mb-8 border-b border-gray-100 pb-4">
              {[
                { key: 'all', label: 'All Content' },
                { key: 'article', label: 'Articles' },
                { key: 'interview', label: 'Interviews' },
                { key: 'spotlight', label: 'Spotlights' },
                { key: 'video', label: 'Videos' },
              ].map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)}
                  className={`px-4 py-2 rounded-full text-sm font-inter font-medium transition-all ${
                    filter === f.key ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`} data-testid={`filter-${f.key}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </FadeUp>

          {/* Content Grid */}
          {loading ? (
            <div className="text-center text-gray-400 font-inter py-12">Loading content...</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item, i) => {
                const tc = TYPE_CONFIG[item.content_type] || TYPE_CONFIG.article;
                const Icon = tc.icon;
                return (
                  <FadeUp key={i}>
                    <motion.div whileHover={{ y: -3 }}
                      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-pointer group overflow-hidden"
                      onClick={() => setSelectedArticle(item)} data-testid={`vault-content-${i}`}>
                      {item.content_type === 'video' && item.media_url ? (
                        <div className="h-40 bg-gray-900 flex items-center justify-center">
                          <Play className="w-10 h-10 text-white/60" />
                        </div>
                      ) : (
                        <div className={`h-1.5 bg-gradient-to-r ${
                          item.content_type === 'interview' ? 'from-rose-400 to-rose-500' :
                          item.content_type === 'spotlight' ? 'from-amber-400 to-amber-500' :
                          'from-indigo-400 to-indigo-500'
                        }`} />
                      )}
                      <div className="p-5">
                        <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-inter font-medium border ${tc.color} mb-3`}>
                          <Icon className="w-3 h-3" /><span>{tc.label}</span>
                        </span>
                        <h3 className="text-lg font-cormorant font-bold text-gray-900 mb-2 group-hover:text-indigo-700 transition-colors leading-snug">{item.title}</h3>
                        <p className="text-gray-500 font-inter text-xs leading-relaxed line-clamp-3 mb-4">{item.description}</p>
                        <div className="flex items-center justify-between text-gray-400 text-xs font-inter">
                          <span>{item.author}</span>
                          <span>{new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        </div>
                      </div>
                    </motion.div>
                  </FadeUp>
                );
              })}
            </div>
          )}

          {/* Article Detail Modal */}
          {selectedArticle && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setSelectedArticle(null)}>
              <div className="bg-white rounded-2xl max-w-2xl w-full my-8 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="article-detail-modal">
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    {(() => {
                      const tc = TYPE_CONFIG[selectedArticle.content_type] || TYPE_CONFIG.article;
                      const Icon = tc.icon;
                      return (
                        <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-inter font-medium border ${tc.color}`}>
                          <Icon className="w-3 h-3" /><span>{tc.label}</span>
                        </span>
                      );
                    })()}
                    <button onClick={() => setSelectedArticle(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                  </div>
                  <h1 className="text-3xl font-cormorant font-bold text-gray-900 mb-3">{selectedArticle.title}</h1>
                  <div className="flex items-center space-x-3 text-gray-400 text-sm font-inter mb-6 pb-6 border-b border-gray-100">
                    <span>{selectedArticle.author}</span>
                    <span>-</span>
                    <span>{new Date(selectedArticle.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  {selectedArticle.content_type === 'video' && selectedArticle.media_url && (
                    <div className="mb-6 rounded-xl overflow-hidden bg-black">
                      <video controls className="w-full" data-testid="vault-video-player">
                        <source src={`${BACKEND_URL}${selectedArticle.media_url}`} />
                      </video>
                    </div>
                  )}
                  <div className="prose prose-gray max-w-none">
                    <p className="text-gray-600 font-inter text-base leading-relaxed whitespace-pre-wrap">{selectedArticle.description}</p>
                    {selectedArticle.body && (
                      <p className="text-gray-600 font-inter text-base leading-relaxed whitespace-pre-wrap mt-4">{selectedArticle.body}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
